import type { ComponentProps, ReactNode } from "react"
import { forwardRef } from "react"

import { cn } from "@productlane/lib/src/cn"
import { inputVariants } from "@productlane/ui/src/components/form/input/input-variants"

import type { HighlightToken } from "./policy-token-highlight"
import {
	HIGHLIGHT_CARET,
	HIGHLIGHT_HIDDEN_TEXT,
	HIGHLIGHT_RING_ERROR,
	HIGHLIGHT_RING_OK,
	HIGHLIGHT_METRICS as METRICS,
} from "./highlight-metrics"
import {
	FieldShell,
	HighlightOverlay,
	PLAIN_TEXT_FIELD,
	useHighlightOverlay,
} from "./highlighted-field"

interface Props extends Omit<ComponentProps<"input">, "children"> {
	tokens: HighlightToken[]
	label?: ReactNode
	errorMessage?: ReactNode
	hint?: ReactNode
	wrapperClassName?: string
	/** Sits inside the box at its trailing edge, for a mark about the value. */
	adornment?: ReactNode
}

export const HighlightedInput = forwardRef<HTMLInputElement, Props>(
	(
		{
			tokens,
			label,
			errorMessage,
			hint,
			wrapperClassName,
			adornment,
			className,
			value,
			...props
		},
		forwardedRef,
	) => {
		const { overlayRef, setRef, composing, syncProps } =
			useHighlightOverlay<HTMLInputElement>({
				value,
				axis: "x",
				forwardedRef,
			})

		// Only the trailing side moves, so no glyph shifts under the caret.
		const trailing = adornment ? "pr-8" : undefined

		return (
			<FieldShell
				label={label}
				htmlFor={props.name}
				hint={hint}
				errorMessage={errorMessage}
				className={wrapperClassName}
			>
				<input
					{...props}
					{...PLAIN_TEXT_FIELD}
					{...syncProps}
					ref={setRef}
					value={value}
					className={cn(
						inputVariants(),
						METRICS,
						trailing,
						!composing && HIGHLIGHT_HIDDEN_TEXT,
						HIGHLIGHT_CARET,
						errorMessage ? HIGHLIGHT_RING_ERROR : HIGHLIGHT_RING_OK,
						className,
					)}
				/>
				<HighlightOverlay
					shape="single"
					tokens={tokens}
					placeholder={props.placeholder}
					composing={composing}
					overlayRef={overlayRef}
					className={trailing}
				/>
				{adornment ? (
					<span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center">
						{adornment}
					</span>
				) : null}
			</FieldShell>
		)
	},
)
